/**
 * Company-ID input that pre-fills an organization's details from its national
 * business register. Owns the register-generic copy; the caller supplies the
 * country-specific label, placeholder and hint.
 */
import { useState } from 'react';
import { Button, Input, InputWrapper } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface CompanyRegistryLookupRowProps {
  id: string;
  label: string;
  placeholder: string;
  hint?: string;
  loading: boolean;
  error: string | null;
  applied: boolean;
  disabled?: boolean;
  /** Wraps the row in a muted card — used where it sits among plain fields. */
  framed?: boolean;
  onApply: (query: string) => void;
  /** Clears the previous error/success so it can't linger over a corrected value. */
  onQueryChange: () => void;
}

export function CompanyRegistryLookupRow({
  id,
  label,
  placeholder,
  hint,
  loading,
  error,
  applied,
  disabled = false,
  framed = false,
  onApply,
  onQueryChange
}: CompanyRegistryLookupRowProps) {
  const t = useFeatureTranslation('shared.companyRegistry');
  const [query, setQuery] = useState('');

  const submit = () => {
    if (query.trim()) onApply(query);
  };

  const row = (
    <>
      <InputWrapper
        label={label}
        htmlFor={id}
        hint={hint}
        isError={!!error}
        errorText={error ?? undefined}
      >
        {/* Own id so InputWrapper doesn't clone the label's id onto this div */}
        <div id={`${id}-row`} className='flex gap-sm'>
          <Input
            id={id}
            type='text'
            value={query}
            placeholder={placeholder}
            disabled={disabled || loading}
            className='flex-1'
            onChange={e => {
              setQuery(e.target.value);
              onQueryChange();
            }}
            // No surrounding <form> here, so Enter would otherwise do nothing.
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <Button
            type='button'
            variant='primary'
            icon='magnifying-glass'
            className='h-10'
            disabled={disabled || loading || !query.trim()}
            onClick={submit}
          >
            {loading ? t('loading') : t('action')}
          </Button>
        </div>
      </InputWrapper>

      {applied && !error && (
        <p role='status' className='text-sm text-success-text mt-xs'>
          {t('applied')}
        </p>
      )}
    </>
  );

  if (!framed) return row;

  return <div className='bg-background-muted border border-border-base rounded-lg p-md'>{row}</div>;
}
